import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, call } from '../../lib/api'
import { customersQuery, ordersQuery, type OrderFilters, type Period } from '../../lib/queries'
import { kg, nairaFull, nairaShort, orderDate } from '../../lib/format'
import { FilterView, Toolbar, tdCls, thCls } from '../../components/filters'
import { FormError } from '../../components/auth'
import { ACTIONS, STATUSES, STATUS_STYLE, StatusPill, type Status } from '../../components/status'
import { kgStat, Avatar, Button, PageHeader, Cylinder, Empty, ErrorBox, Field, Icon, Input, Menu, MenuItem, Modal, Pagination, PeriodSelect, Skeleton, StatCard, Tag, cx, levelWord, toast, useMedia } from '../../components/ui'

export const Route = createFileRoute('/_app/orders')({
  validateSearch: (s: Record<string, unknown>): OrderFilters => ({
    state: (s.state as string) || undefined, region: (s.region as string) || undefined, outletId: (s.outletId as string) || undefined,
    q: (s.q as string) || undefined, page: Number(s.page) > 1 ? Number(s.page) : undefined,
    status: STATUSES.includes(s.status as Status) ? (s.status as string) : undefined,
    period: ['today', 'week', 'quarter', 'year'].includes(s.period as string) ? (s.period as Period) : undefined,
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.prefetchQuery(ordersQuery(deps)),
  component: Orders,
})

function Orders() {
  const f = Route.useSearch()
  const navigate = Route.useNavigate()
  const set = (patch: Partial<OrderFilters>) => navigate({ search: (s) => ({ ...s, page: undefined, ...patch }), replace: true })
  const qc = useQueryClient()
  const { data, error, refetch, isPlaceholderData } = useQuery(ordersQuery(f))
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [menu, setMenu] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const refresh = () => ['orders', 'dashboard', 'customers', 'customer', 'outlet'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }))

  const move = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Status }) => call(api.orders[':id'].$patch({ param: { id }, json: { status } })),
    onSuccess: (_, v) => (toast(`Order ${STATUS_STYLE[v.status].label.toLowerCase()}`), refresh()),
    onError: (e) => toast(e.message, 'error'),
  })
  const bulk = useMutation({
    mutationFn: (status: 'approved' | 'rejected') => call(api.orders.bulk.$post({ json: { ids: [...selected], status } })),
    onSuccess: (r, status) => (toast(`${r.updated} order${r.updated === 1 ? '' : 's'} ${status}`), setSelected(new Set()), refresh()),
    onError: (e) => toast(e.message, 'error'),
  })

  const k = data?.kpis
  const narrow = useMedia('(max-width: 639px)')
  const toggle = (id: string) => setSelected((s) => (s.has(id) ? (s.delete(id), new Set(s)) : new Set(s.add(id))))

  return (
    <div>
      <PageHeader title="Orders" subtitle="Refill orders from meters, the customer app and your team."
        actions={<>
          <PeriodSelect value={f.period ?? 'month'} onChange={(p) => set({ period: p === 'month' ? undefined : p })} />
          <Button onClick={() => setAdding(true)}><Icon name="add" className="size-4" />New order</Button>
        </>} />
      <FilterView value={f} onChange={(p) => set({ state: p.state, region: p.region, outletId: p.outletId })} section="Orders" icon="nav-orders" />

      <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5 xl:gap-6">
        {k ? (
          <>
            <StatCard tone="peach" label="Revenue" value={nairaShort(k.revenueKobo)} sub={nairaFull(k.revenueKobo)} change={k.revenueChange} />
            <StatCard label="Volume sold" value={kgStat(k.volumeKg)} change={k.volumeChange} />
            <StatCard label={<>Fulfilled<span className="font-normal">/Failed</span> Orders</>} value={<>{k.fulfilled}<span className="font-normal">/{k.failed}</span></>} />
            <StatCard tone="peach" label={<>Active<span className="font-normal">/New</span> Customers</>} value={<>{k.activeCustomers}<span className="font-normal">/{k.newCustomers}</span></>} />
            <StatCard tone="peach" label="Receivables" value={nairaShort(k.receivablesKobo)} sub={nairaFull(k.receivablesKobo)} />
          </>
        ) : Array.from({ length: 5 }, (_, i) => <Skeleton key={i} className="h-[112px] rounded-card" />)}
      </div>

      <div className="mt-10 mb-3 flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-semibold">Orders List</h2>
        <div className="flex flex-wrap gap-1 text-xs">
          {[undefined, ...STATUSES].map((s) => (
            <button key={s ?? 'all'} onClick={() => set({ status: s })} className={cx('rounded-full px-2.5 py-1', f.status === s ? 'bg-ink text-white' : 'bg-white/60 hover:bg-white')}>
              {s ? STATUS_STYLE[s].label : 'All'}
            </button>
          ))}
        </div>
      </div>
      <Toolbar search={f.q ?? ''} onSearch={(q) => set({ q: q || undefined })}>
        {selected.size > 0 && (
          <span className="flex items-center gap-2 pl-2 text-xs">
            {selected.size} selected
            <Button className="h-7 px-2.5 text-xs" disabled={bulk.isPending} onClick={() => bulk.mutate('approved')}>Approve</Button>
            <Button variant="light" className="h-7 px-2.5 text-xs" disabled={bulk.isPending} onClick={() => bulk.mutate('rejected')}>Reject</Button>
          </span>
        )}
      </Toolbar>

      {error ? <div className="mt-4"><ErrorBox error={error} onRetry={refetch} /></div> : !data ? <Skeleton className="mt-2 h-96" /> : (
        <div className={cx('transition-opacity', isPlaceholderData && 'opacity-60')}>
          {data.orders.length === 0 ? <Empty title="No orders here">Orders appear when a meter drops below its trigger, a customer requests a refill, or you add one.</Empty> : narrow ? (
            // Phones get cards, same as the Customers list.
            <ul className="mt-3 space-y-3">
              {data.orders.map((o) => (
                <li key={o.id} className="rounded-card bg-white p-4">
                  <div className="flex items-center justify-between gap-3">
                    <Link to="/customers/$customerId" params={{ customerId: o.customer.id }} className="flex min-w-0 items-center gap-2 text-sm font-semibold">
                      <Avatar name={o.customer.name} photoKey={o.customer.photoKey} /><span className="truncate">{o.customer.name}</span>
                    </Link>
                    <StatusPill status={o.status as Status} />
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="num font-medium">{kg(o.kg)} · {nairaShort(o.amountKobo)}{o.source === 'auto' && <span className="ml-1.5 text-muted">auto</span>}</span>
                    <span className="text-muted">{orderDate(o.createdAt)}</span>
                  </div>
                  <p className="mt-1 flex items-center gap-2 text-xs text-ink-2"><Cylinder level={o.customer.level} />{levelWord(o.customer.level)}, {o.customer.levelKg}/{kg(o.customer.cylinderKg)}</p>
                  {ACTIONS[o.status as Status].length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {ACTIONS[o.status as Status].map(([st, label]) => (
                        <Button key={st} variant={st === 'rejected' || st === 'failed' ? 'light' : 'primary'} className="h-8 px-3 text-xs" disabled={move.isPending} onClick={() => move.mutate({ id: o.id, status: st })}>{label}</Button>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="mt-2 overflow-x-auto bg-white">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr>
                    <th className={cx(thCls, 'w-8 pr-0')}><span className="sr-only">Select</span></th>
                    <th className={thCls}>User</th>
                    <th className={thCls}>Estimated Tank Storage</th>
                    <th className={thCls}>Order</th>
                    <th className={thCls}>Last Order</th>
                    <th className={thCls}>Gas level</th>
                    <th className={thCls}>Order Status</th>
                    <th className={cx(thCls, 'w-10')}><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {data.orders.map((o) => {
                    const actions = ACTIONS[o.status as Status]
                    return (
                      <tr key={o.id} className={cx('group border-b border-black/5 last:border-0 hover:bg-page/70', selected.has(o.id) && 'bg-page/70')}>
                        <td className={cx(tdCls, 'pr-0')}>
                          {(o.status === 'pending' || o.status === 'approved') && (
                            <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} className="size-3.5 accent-brand" aria-label={`Select order for ${o.customer.name}`} />
                          )}
                        </td>
                        <td className={tdCls}>
                          <Link to="/customers/$customerId" params={{ customerId: o.customer.id }} className="flex items-center gap-2 hover:text-brand">
                            <Avatar name={o.customer.name} photoKey={o.customer.photoKey} />{o.customer.name}{o.customer.isRetailAgent && <Tag>Retail Agent</Tag>}
                          </Link>
                        </td>
                        <td className={tdCls}>{kg(o.customer.cylinderKg)}</td>
                        <td className={cx(tdCls, 'num')}>{kg(o.kg)} · {nairaShort(o.amountKobo)}{o.source === 'auto' && <span className="ml-1.5 text-[10px] text-muted">auto</span>}</td>
                        <td className={tdCls}><span className="flex items-center gap-1.5"><Icon name="calendar" className="size-4" />{orderDate(o.createdAt)}</span></td>
                        <td className={tdCls}><span className="flex items-center gap-2"><Cylinder level={o.customer.level} /><span className="num">{levelWord(o.customer.level)}, {o.customer.levelKg}/{kg(o.customer.cylinderKg)}</span></span></td>
                        <td className={tdCls}><StatusPill status={o.status as Status} /></td>
                        <td className={cx(tdCls, 'relative')}>
                          {actions.length > 0 && (
                            <>
                              <button onClick={() => setMenu(menu === o.id ? null : o.id)} className="grid size-7 place-items-center rounded-lg opacity-60 group-hover:opacity-100 hover:bg-black/5" aria-label="Order actions">
                                <Icon name="dots-h" className="size-4" />
                              </button>
                              {menu === o.id && (
                                <div className="absolute right-12 z-20">
                                  <Menu onClose={() => setMenu(null)}>
                                    {actions.map(([s, label]) => <MenuItem key={s} onClick={() => (setMenu(null), move.mutate({ id: o.id, status: s }))}>{label}</MenuItem>)}
                                  </Menu>
                                </div>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          <Pagination page={data.page} total={data.total} pageSize={data.pageSize} onPage={(page) => navigate({ search: (s) => ({ ...s, page: page > 1 ? page : undefined }) })} />
        </div>
      )}

      <Modal open={adding} onClose={() => setAdding(false)} title="New order">
        <NewOrder onDone={() => (setAdding(false), refresh())} />
      </Modal>
    </div>
  )
}

function NewOrder({ onDone }: { onDone: () => void }) {
  const [q, setQ] = useState('')
  const [pick, setPick] = useState<{ id: string; name: string; room: number } | null>(null)
  const [amount, setAmount] = useState('')
  const { data } = useQuery({ ...customersQuery({ q, page: 1 }), enabled: q.length > 1 && !pick })
  const create = useMutation({
    mutationFn: () => call(api.orders.$post({ json: { customerId: pick!.id, kg: Number(amount) } })),
    onSuccess: () => (toast('Order created'), onDone()),
  })
  return (
    <form onSubmit={(e) => (e.preventDefault(), create.mutate())} className="space-y-4">
      <FormError error={create.error?.message} />
      {pick ? (
        <div className="flex items-center justify-between rounded-lg bg-page px-3 py-2 text-sm">
          <span>{pick.name}</span>
          <button type="button" onClick={() => setPick(null)} className="text-brand">Change</button>
        </div>
      ) : (
        <Field label="Customer">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or phone" autoFocus />
          {data && (
            <ul className="mt-1 max-h-56 overflow-y-auto rounded-lg border border-line">
              {data.customers.map((c) => (
                <li key={c.id}>
                  <button type="button" onClick={() => (setPick({ id: c.id, name: c.name, room: c.cylinderKg - c.levelKg }), setAmount(String(Math.round((c.cylinderKg - c.levelKg) * 10) / 10)))} className="flex w-full justify-between px-3 py-2 text-left text-sm hover:bg-page">
                    <span>{c.name}</span><span className="text-xs text-muted">{c.levelKg}/{kg(c.cylinderKg)}</span>
                  </button>
                </li>
              ))}
              {!data.customers.length && <li className="px-3 py-2 text-sm text-muted">No match</li>}
            </ul>
          )}
        </Field>
      )}
      <Field label="Quantity (kg)"><Input type="number" step="0.1" min="0.1" value={amount} onChange={(e) => setAmount(e.target.value)} required /></Field>
      <div className="flex justify-end"><Button disabled={!pick || !Number(amount) || create.isPending}>Create order</Button></div>
    </form>
  )
}
